import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Editable from '../Editable';
import { useSection } from '../useSection';
import { authFetch } from '../../auth';

const API = '/api/cms/history';

let _tmpSeq = 0;
const nextTmp = () => `tmp-${Date.now()}-${_tmpSeq++}`;
const MONTH_LABELS = ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'];

/**
 * 연혁 — DB(/api/cms/history) 편집.
 * 데이터는 평탄한 행(행마다 period/year/month/entry 텍스트). 탭=서로 다른 period 문자열.
 * 탭(period)·연도·월그룹·항목을 추가/수정/삭제하고, 목록 위 제목·부제는 history/ui 섹션으로 저장한다.
 */
export default function History({ onRegisterSave, onRegisterDirty, refreshKey = 0 }) {
  const [rows, setRows] = useState(null);
  const [tab, setTab] = useState(null);
  const ref = useRef([]);
  const delRef = useRef([]); // 저장 시 삭제할 intNum 목록(삭제 예약)
  const { data: ui, setField, saveSection, isDirty: uiIsDirty } = useSection('history', 'ui', 1);

  useEffect(() => {
    let alive = true;
    authFetch(`${API}?lang=ko`).then((r) => r.json())
      .then((d) => {
        if (!alive) return;
        const mapped = d.map((i) => ({
          intNum: i.intNum != null ? i.intNum : null, _tmpId: nextTmp(),
          period: i.period || '', year: i.year || '', month: i.month || '', entry: i.entry || '',
          sort: i.sort != null ? i.sort : 0, _dirty: false,
        }));
        ref.current = mapped; setRows(mapped); setTab(mapped[0] ? mapped[0].period : null);
      })
      .catch(() => { if (alive) { ref.current = []; setRows([]); } });
    return () => { alive = false; };
  }, [refreshKey]);

  const commit = (next) => { ref.current = next; setRows(next); };
  const rid = (r) => (r.intNum != null ? `h-${r.intNum}` : r._tmpId);
  const nextSort = () => ref.current.reduce((mx, r) => Math.max(mx, r.sort || 0), 0) + 1;
  const minSortOf = (period) => ref.current.filter((r) => r.period === period).reduce((mn, r) => Math.min(mn, r.sort || 0), 0);

  const periods = useMemo(() => {
    if (!rows) return [];
    const seen = new Set(); const out = [];
    rows.forEach((i) => { if (i.period && !seen.has(i.period)) { seen.add(i.period); out.push(i.period); } });
    return out;
  }, [rows]);

  // 현재 기간(tab) 그룹: 연도 → 월그룹 → 항목(편집을 위해 행 전체를 보관)
  const years = useMemo(() => {
    if (!rows) return [];
    const ys = [];
    rows.filter((i) => i.period === tab).forEach((i) => {
      let y = ys.find((x) => x.year === i.year);
      if (!y) { y = { year: i.year, months: [] }; ys.push(y); }
      let m = y.months.find((x) => x.month === i.month);
      if (!m) { m = { month: i.month, entries: [] }; y.months.push(m); }
      m.entries.push(i);
    });
    return ys;
  }, [rows, tab]);

  // ── 인라인 수정 (변경된 행에 _dirty 표시) ──
  const updateRow = (id, patch) => commit(ref.current.map((r) => (rid(r) === id ? { ...r, ...patch, _dirty: true } : r)));
  const renamePeriod = (oldP, newP) => {
    if (!newP || newP === oldP) return;
    commit(ref.current.map((r) => (r.period === oldP ? { ...r, period: newP, _dirty: true } : r)));
    if (tab === oldP) setTab(newP);
  };
  const renameYear = (period, oldYear, newYear) => {
    if (newYear === oldYear) return;
    commit(ref.current.map((r) => (r.period === period && r.year === oldYear ? { ...r, year: newYear, _dirty: true } : r)));
  };
  const renameMonth = (period, year, oldMonth, newMonth) => {
    if (newMonth === oldMonth) return;
    commit(ref.current.map((r) => (r.period === period && r.year === year && r.month === oldMonth ? { ...r, month: newMonth, _dirty: true } : r)));
  };

  // ── 삭제 (예약만; 저장 시 일괄 DELETE. 저장 전이면 로컬에서만 제거되어 되돌릴 수 있음) ──
  const deleteRows = (predicate, confirmMsg) => {
    const targets = ref.current.filter(predicate);
    if (!targets.length) return false;
    if (confirmMsg && !window.confirm(confirmMsg)) return false;
    targets.forEach((r) => { if (r.intNum != null) delRef.current.push(r.intNum); }); // 저장 시 삭제 예약
    const removed = new Set(targets.map(rid));
    commit(ref.current.filter((r) => !removed.has(rid(r))));
    return true;
  };
  const delPeriod = (p) => {
    const ok = deleteRows((r) => r.period === p, `"${p}" 탭과 그 안의 모든 연혁을 삭제할까요? (저장 시 반영)`);
    if (ok && tab === p) setTab((ref.current[0] && ref.current[0].period) || null);
  };
  const delYear = (period, year) => deleteRows((r) => r.period === period && r.year === year, '이 연도 전체를 삭제할까요? (저장 시 반영)');
  const delMonthGroup = (period, year, month) => deleteRows((r) => r.period === period && r.year === year && r.month === month, '이 월 그룹 전체를 삭제할까요? (저장 시 반영)');
  const delEntry = (row) => deleteRows((r) => rid(r) === rid(row), null);

  // ── 추가 ──
  const addPeriod = () => {
    let name = '새 탭'; let n = 1;
    const existing = new Set(ref.current.map((r) => r.period));
    while (existing.has(name)) { n += 1; name = `새 탭 ${n}`; }
    const nr = { intNum: null, _tmpId: nextTmp(), period: name, year: '새 연도', month: '1월', entry: '새 연혁 항목', sort: nextSort(), _dirty: true };
    commit([...ref.current, nr]);
    setTab(name);
  };
  const distinctYearsOf = (period) => [...new Set(ref.current.filter((r) => r.period === period).map((r) => r.year))];
  // 겹치면 한 그룹으로 합쳐지므로, placeholder를 고유하게 부여(특정 연도값은 자동으로 넣지 않음 — 사용자가 직접 입력)
  const uniqueYearPlaceholder = (used) => { let y = '새 연도'; let k = 1; while (used.has(y)) { k += 1; y = `새 연도 ${k}`; } return y; };

  // 연도 추가 — 한 탭에는 연도 그룹 최대 3개. 초과 시 새 탭을 자동 생성해 거기에 추가.
  const YEARS_PER_TAB = 3;
  const addYear = () => {
    const period = tab || '연혁';
    const used = new Set(distinctYearsOf(period));
    if (used.size < YEARS_PER_TAB) {
      const nr = { intNum: null, _tmpId: nextTmp(), period, year: uniqueYearPlaceholder(used), month: '1월', entry: '새 연혁 항목', sort: minSortOf(period) - 1, _dirty: true };
      commit([nr, ...ref.current]); // 배열 맨 앞 → 현재 탭 그룹 최상단(탭 바로 아래)에 표시되어 바로 보임
      if (!tab) setTab(period);
      return;
    }
    // 3개 꽉 참 → 새 탭(placeholder) 생성 — 탭 라벨·연도는 비워두고 사용자가 직접 입력
    const existing = new Set(ref.current.map((r) => r.period));
    let newPeriod = '새 탭'; let n = 1;
    while (existing.has(newPeriod)) { n += 1; newPeriod = `새 탭 ${n}`; }
    const globalMinSort = ref.current.reduce((mn, r) => Math.min(mn, r.sort || 0), 0);
    const nr = { intNum: null, _tmpId: nextTmp(), period: newPeriod, year: '새 연도', month: '1월', entry: '새 연혁 항목', sort: globalMinSort - 1, _dirty: true };
    commit([nr, ...ref.current]); // 맨 앞 → 새 탭이 최신(맨 왼쪽)으로 표시
    setTab(newPeriod);
  };
  // 월 그룹 추가 — 한 연도에는 월 그룹 최대 12개(1~12월). 초과 차단. (month-item 항목은 무제한)
  const MONTHS_PER_YEAR = 12;
  const distinctMonthsOf = (period, year) => [...new Set(ref.current.filter((r) => r.period === period && r.year === year).map((r) => r.month))];
  const addMonthGroup = (period, year) => {
    const used = new Set(distinctMonthsOf(period, year));
    if (used.size >= MONTHS_PER_YEAR) {
      window.alert(`한 연도에는 월 그룹을 최대 ${MONTHS_PER_YEAR}개까지만 추가할 수 있습니다.`);
      return;
    }
    // 라벨이 겹치면 한 그룹으로 합쳐지므로 미사용 N월을 고유하게 부여
    const month = MONTH_LABELS.find((m) => !used.has(m)) || '새 월';
    const nr = { intNum: null, _tmpId: nextTmp(), period, year, month, entry: '새 연혁 항목', sort: nextSort(), _dirty: true };
    commit([...ref.current, nr]);
  };
  const addEntry = (period, year, month) => {
    const nr = { intNum: null, _tmpId: nextTmp(), period, year, month, entry: '새 연혁 항목', sort: nextSort(), _dirty: true };
    commit([...ref.current, nr]);
  };

  const save = useCallback(async () => {
    // 예약된 삭제 먼저 반영
    for (const id of delRef.current) {
      const res = await authFetch(`${API}/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`연혁 삭제 실패 (${res.status})`);
    }
    delRef.current = [];
    for (const r of ref.current) {
      if (!r._dirty) continue;                            // 변경된 행만 저장
      if (!r.entry && !r.month && !r.year) continue;       // 완전히 빈 행은 건너뜀
      const res = await authFetch(API, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intNum: r.intNum, period: r.period, year: r.year, month: r.month, entry: r.entry, sort: r.sort }),
      });
      if (!res.ok) throw new Error(`연혁 저장 실패 (${res.status})`);
    }
    await saveSection(); // 제목·부제(history/ui)
  }, [saveSection]);

  useEffect(() => { if (onRegisterSave) onRegisterSave(save); return () => { if (onRegisterSave) onRegisterSave(null); }; }, [save, onRegisterSave]);

  // 미저장 변경 여부(저장 가드용): 변경된 행 · 삭제 예약 · 문구 변경 중 하나라도 있으면 true
  const isDirty = useCallback(() => ref.current.some((r) => r._dirty) || delRef.current.length > 0 || uiIsDirty(), [uiIsDirty]);
  useEffect(() => { if (onRegisterDirty) onRegisterDirty(isDirty); return () => { if (onRegisterDirty) onRegisterDirty(null); }; }, [isDirty, onRegisterDirty]);

  if (rows === null) {
    return <section className="section pt-page"><div className="container"><p className="heading-sub color-muted">불러오는 중…</p></div></section>;
  }

  return (
    <section className="section pt-page">
      <div className="container">
        <Editable as="h1" className="heading-page" value={ui.title || '연혁 & 실적'} onChange={(t) => setField('title', t)} />
        <Editable as="p" className="heading-sub color-muted" value={ui.intro || '임베디드 컴퓨팅, 국방 시스템, 산업 기술 분야에서 30년 이상의 혁신 — 1994년부터 현재까지.'} onChange={(t) => setField('intro', t)} />

        <div className="history-tabs-row">
          <button type="button" className="row-add-btn" onClick={addYear}>+ 연도 추가</button>
          <div className="history-tabs" role="tablist" aria-label="연혁 기간">
            {periods.map((p) => (
              <div key={p} className={`history-tab edit-tab${p === tab ? ' active' : ''}`} role="tab" aria-selected={p === tab}>
                <Editable as="span" value={p} onClick={() => setTab(p)} onChange={(t) => renamePeriod(p, t)} />
                <button type="button" className="tab-del-btn" title="탭(기간) 삭제" onClick={() => delPeriod(p)}>×</button>
              </div>
            ))}
          </div>
        </div>

        <div className="history-panel active" role="tabpanel">
          {years.length === 0 ? (
            <p className="heading-sub color-muted" style={{ padding: '2rem 0' }}>이 기간에 연혁이 없습니다. “+ 연도 추가”로 시작하세요.</p>
          ) : years.map((yg) => (
            <div className="history-year-group visible" key={`${tab}-${yg.year}`}>
              <div className="history-year-head">
                <Editable as="h2" className="history-year" value={yg.year} onChange={(t) => renameYear(tab, yg.year, t)} />
                <button type="button" className="row-del-btn" title="연도 삭제" onClick={() => delYear(tab, yg.year)}>×</button>
              </div>
              <div className="history-entries">
                {yg.months.map((m) => (
                  <div className="h-month-group" key={`${yg.year}-${m.month}`}>
                    <div className="h-month-col">
                      <Editable as="div" className="h-month" value={m.month} onChange={(t) => renameMonth(tab, yg.year, m.month, t)} />
                      <button type="button" className="row-del-btn sm" title="월 그룹 삭제" onClick={() => delMonthGroup(tab, yg.year, m.month)}>×</button>
                    </div>
                    <div className="h-month-items">
                      {m.entries.map((e) => (
                        <div className="h-entry" key={rid(e)}>
                          <Editable as="p" value={e.entry} onChange={(t) => updateRow(rid(e), { entry: t })} />
                          <button type="button" className="row-del-btn sm" title="항목 삭제" onClick={() => delEntry(e)}>×</button>
                        </div>
                      ))}
                      <button type="button" className="row-add-btn sm" onClick={() => addEntry(tab, yg.year, m.month)}>+ 항목(h-month-item) 추가</button>
                    </div>
                  </div>
                ))}
                {yg.months.length < 12 ? (
                  <button type="button" className="row-add-btn" onClick={() => addMonthGroup(tab, yg.year)}>+ 월 그룹(h-month-group) 추가</button>
                ) : (
                  <p className="history-cap-note">월 그룹은 최대 12개입니다.</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
